import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router';
import { useAuth } from '@/hooks/useAuth';
import { useProposal } from '@/context/ProposalContext';
import { useIsMobile } from '@/hooks/use-mobile';
import {
  LayoutDashboard, SlidersHorizontal, TrendingUp, FileBarChart,
  Users, TestTube, Scale, FlaskConical, Database, Shield,
  ChevronLeft, ChevronRight, LogOut, UserCircle, Server,
  Droplets, Beaker, KeyRound, List, Workflow, ShieldAlert, Microscope,
  Sparkles, Settings, BookOpen, FileText, X, ShieldCheck, Receipt, Waves,
  Wind, Layers, Grid3X3, ClipboardCheck, MapPin, ClipboardList, Mountain,
  Thermometer, Leaf, BarChart3, Upload, Wrench, Volume2, Activity,
  Boxes, AlertOctagon, PoundSterling, GitBranch, Box, Calculator,
  Atom, Sigma, CircleDot, Bug, Magnet, Moon, FolderOpen, Zap, CalendarClock,
} from 'lucide-react';

interface NavItem {
  label: string;
  path?: string;
  icon?: React.ElementType;
  admin?: boolean;
  /** Render as a non-clickable subsection header inside a group (no path/icon). */
  header?: boolean;
}

interface NavGroup {
  title: string;
  items: NavItem[];
  admin?: boolean;
  /** Visual tint applied to group title and items. `blue` flags Reservoir Project, `yellow` flags an active customer/job. */
  tint?: 'blue' | 'yellow' | 'default';
  /** Render the group title as a click-to-expand dropdown. Default collapsed. */
  collapsible?: boolean;
  /** When collapsible, default to expanded? */
  defaultExpanded?: boolean;
}

const navGroups: NavGroup[] = [
  {
    title: 'Overview',
    items: [
      { label: 'Dashboard', path: '/', icon: LayoutDashboard },
      { label: 'Showcase', path: '/advert', icon: Sparkles },
      { label: 'Pipe Flow Calculator', path: '/pipe-flow-calculator', icon: Calculator },
    ],
  },
  {
    title: 'Water Sampling',
    items: [
      { label: 'Add Influent Sample', path: '/influent-entry', icon: Droplets },
      { label: 'Influent Records', path: '/influent', icon: Droplets },
      { label: 'Influent Trends', path: '/influent-trends', icon: TrendingUp },
      { label: 'Influent Study', path: '/influent-study', icon: Microscope },
      { label: 'Add Effluent Sample', path: '/effluent-entry', icon: Beaker },
      { label: 'Discharge Records', path: '/effluent', icon: Beaker },
      { label: 'Effluent Trends', path: '/effluent-trends', icon: TrendingUp },
      { label: 'Effluent Reports', path: '/effluent-reports', icon: Beaker },
      { label: 'Effluent Study', path: '/effluent-study', icon: Microscope },
      { label: 'Compliance Gateway', path: '/compliance-gateway', icon: ShieldCheck },
      { label: 'Sample Workflow', path: '/sample-workflow', icon: BookOpen },
      { label: 'Customer Site Hub', path: '/customer-site', icon: Database },
      { label: 'Lab Worksheet', path: '/lab-worksheet', icon: FileText },
      { label: 'Compliance Dashboard', path: '/compliance-dashboard', icon: ShieldCheck },
    ],
  },
  {
    title: 'Assessment',
    items: [
      { label: 'Site Assessment', path: '/parameters', icon: SlidersHorizontal },
      { label: 'Draft Influent Entry', path: '/site-assessment', icon: FlaskConical },
      { label: 'Dosing', path: '/dosing', icon: FlaskConical },
    ],
  },
  {
    title: '★ Reservoir ▾',
    tint: 'blue',
    collapsible: true,
    defaultExpanded: false,
    items: [
      // Hub & orientation
      { header: true, label: 'Hub & Orientation' },
      { label: 'Reservoir Hub',        path: '/reservoir-hub',           icon: Waves },
      { label: 'Reservoir Management', path: '/reservoir-management',    icon: Waves },
      { header: true, label: 'Site & Conditions' },
      { label: 'Site Visit Form',      path: '/site-data-capture',       icon: ClipboardList },
      { label: 'Simulation Data Hub',  path: '/simulation-data',         icon: Database },
      { label: 'Data Pack (public)',   path: '/reservoir-data-pack',     icon: BookOpen },
      { label: 'Bathymetry Import',    path: '/bathymetry-import',       icon: Upload },
      { header: true, label: 'Diffuser Design' },
      { label: 'Reservoir Aeration',   path: '/reservoir-aeration-spec', icon: Wind },
      { label: 'Diffuser Module',      path: '/diffuser-module',         icon: Layers },
      { label: 'Custom Diffuser',      path: '/custom-diffuser',         icon: Sparkles },
      { label: 'Diffuser System (loops)', path: '/custom-diffuser-system', icon: Sparkles },
      { label: 'Diffuser Comparison',  path: '/diffuser-comparison',     icon: Grid3X3 },
      { label: 'Pore Release Geometry',path: '/pore-release-geometry',   icon: Wind },
      { label: 'Fouling Drift (10y)',  path: '/fouling-drift',           icon: TrendingUp },
      { header: true, label: 'Array & Layout' },
      { label: 'Module Array',         path: '/module-array',            icon: Grid3X3 },
      { label: 'Reservoir Layout',     path: '/reservoir-layout',        icon: MapPin },
      { label: 'Plume Coverage Map',   path: '/plume-coverage',          icon: MapPin },
      { label: 'Sampling Plan',        path: '/sampling-plan',           icon: TestTube },
      { header: true, label: 'Reservoir Physics' },
      { label: 'Sediment Bed Physics', path: '/sediment-bed-physics',    icon: Mountain },
      { label: 'Bed Deep Assessment',  path: '/bed-assessment',          icon: FlaskConical },
      { label: 'Thermal Model',        path: '/thermal-model',           icon: Thermometer },
      { label: 'Algal Dynamics',       path: '/algal-dynamics',          icon: FlaskConical },
      { label: 'WQ Predictor (live)',  path: '/water-quality-predictor', icon: Activity },
      { label: 'GHG Balance',          path: '/ghg-balance',             icon: Leaf },
      { header: true, label: 'Validation' },
      { label: 'Calculation Chain',    path: '/calculation-chain',       icon: Workflow },
      { label: 'Calculation Steps',    path: '/reservoir-steps',         icon: ClipboardList },
      { label: 'Validation Steps',     path: '/validation-steps',        icon: ClipboardCheck },
      { label: 'Reservoir Validation', path: '/reservoir-validation',    icon: ClipboardCheck },
      { label: 'Diffuser Validation',  path: '/diffuser-validation',     icon: ClipboardCheck },
      { label: 'Sensitivity (Tornado)',path: '/sensitivity',             icon: BarChart3 },
      { header: true, label: 'Engineering' },
      { label: 'Blower Selection',     path: '/blower-selection',        icon: Wind },
      { label: 'Acoustic Noise',       path: '/acoustic-noise',          icon: Volume2 },
      { header: true, label: 'Compliance & Risk' },
      { label: 'Risk Register',        path: '/risk-register',           icon: ShieldAlert },
      { label: 'Reservoir Compliance', path: '/reservoir-compliance',    icon: ShieldCheck },
      { label: 'Pilot Trial Planner',  path: '/pilot-trial-plan',        icon: FlaskConical },
      { header: true, label: 'O & M' },
      { label: 'O&M Schedule',         path: '/om-schedule',             icon: Wrench },
      { header: true, label: 'Reports' },
      { label: 'MASTER REPORT',        path: '/reservoir-master',        icon: FileText },
      { label: 'PDF Index',            path: '/pdf-index',               icon: FileText },
      { label: 'Citations',            path: '/citations',               icon: BookOpen },

      // ── Modules & scenarios (folded in from the former 'Modules' group) ──
      { header: true, label: 'Modules & Scenarios' },
      { label: 'Modules Hub',          path: '/modules',                       icon: Boxes },
      { label: 'Module Trains Guide',  path: '/module-trains',                 icon: Workflow },
      { label: 'Benchmark Sites',      path: '/benchmark-sites',               icon: Database },
      { label: 'Aeration Knowledge',   path: '/aeration-knowledge',            icon: BookOpen },
      { label: 'Perfect Scenario',     path: '/perfect-scenario',              icon: Sparkles },
      { label: 'Scenario Manager',     path: '/scenarios',                     icon: FolderOpen },
      { label: 'Defensibility Statement', path: '/defensibility',              icon: ShieldCheck },
      { label: 'Perfect Diffuser (Barcombe)', path: '/perfect-diffuser',       icon: Wrench },
      { label: 'Aeration Config (active scenario)', path: '/aeration-config',  icon: Wind },
      // Individual module pages (30+) live inside the Modules Hub, grouped by
      // topic (Plume / Bed / Hypolimnion O2 / Algae / Nutrients / Mixing
      // strategy / Operational scheduler / Optimisation / 3rd-party model).
      // Removed from sidebar 2026-05 to declutter; all reachable from Hub.

      // ── Year-Round Strategies (folded in from its own group) ──
      { header: true, label: 'Year-Round Strategies' },
      { label: 'Strategies Hub', path: '/scenarios/year-round', icon: CalendarClock },
      { label: 'A — Continuous Full', path: '/scenarios/year-round/strategy-a-continuous', icon: Activity },
      { label: 'B — Summer Full-Mix', path: '/scenarios/year-round/strategy-b-summer-fullmix', icon: Activity },
      { label: 'C — Adaptive Scheduler', path: '/scenarios/year-round/strategy-c-adaptive', icon: Activity },
      { label: 'C — Defensibility', path: '/scenarios/year-round/strategy-c-defensibility', icon: ShieldCheck },
      { label: 'D — TEM Only Summer', path: '/scenarios/year-round/strategy-d-tem-only', icon: Activity },
    ],
  },
  {
    title: 'Research',
    items: [
      { label: 'Chemical Dosing',      path: '/chemical-dosing',       icon: FlaskConical },
      { label: 'Impeller Mixing',      path: '/impeller-mixing',       icon: Waves },
      { label: 'Dosing & Mixing Train',path: '/dosing-mixing-train',   icon: Workflow },
    ],
  },
  {
    title: 'Equipment',
    items: [
      { label: 'Equipment List', path: '/equipment-list', icon: List },
      { label: 'Equipment Validation', path: '/equipment-validation', icon: Microscope },
      { label: 'Process Train', path: '/process-train', icon: Workflow },
      { label: 'Train Editor (live sim)', path: '/train-edit', icon: Sparkles },
      { label: 'Proposals', path: '/proposals', icon: BookOpen },
      { label: 'Process DB', path: '/process-db', icon: Database },
      { label: 'Cascade Aerator', path: '/cascade-aerator-spec', icon: Droplets },
      { label: 'Ammonia Stripper', path: '/ammonia-stripper-spec', icon: FlaskConical },
      { label: 'Desalination DAF', path: '/desalination-daf-spec', icon: Droplets },
      { label: 'Electrochlorination', path: '/electrochlorination-spec', icon: Zap },
      { label: 'Multimedia Filter', path: '/multimedia-spec', icon: Layers },
      { label: 'GAC Filter', path: '/gac-spec', icon: Layers },
      { label: 'Aeration Column (Fe ox.)', path: '/aeration-column-spec', icon: Wind },
      { label: 'Greensand / Birm (Fe·Mn)', path: '/greensand-spec', icon: Layers },
      { label: 'Ultrafiltration (UF)', path: '/uf-spec', icon: CircleDot },
      { label: 'UV Disinfection', path: '/uv-spec', icon: Zap },
      { label: 'Orifice (Cavitation/Noise)', path: '/orifice-spec', icon: CircleDot },
    ],
  },
  {
    title: '★ MYLAR — BOREHOLE PRE-TREATMENT',
    tint: 'yellow',
    items: [
      { label: 'David Assessment', path: '/mylar-borehole-assessment', icon: ClipboardCheck },
      { label: 'Aeration Column', path: '/aeration-column-spec', icon: Wind },
      { label: 'Greensand Plus', path: '/greensand-spec', icon: Layers },
      { label: 'Ultrafiltration', path: '/uf-spec', icon: CircleDot },
      { label: 'Multimedia (Option A)', path: '/multimedia-spec', icon: Layers },
      { label: 'Customers', path: '/customers', icon: Users },
      { label: 'Process Train', path: '/process-train', icon: Workflow },
      { label: 'Proposals', path: '/proposals', icon: BookOpen },
    ],
  },
  {
    title: 'Data & Reports',
    items: [
      { label: 'Customers', path: '/customers', icon: Users },
      { label: 'Customer Locations', path: '/customer-locations', icon: MapPin },
      { label: 'Samples', path: '/samples', icon: TestTube },
      { label: 'Trends', path: '/trends', icon: TrendingUp },
      { label: 'Reports', path: '/reports', icon: FileBarChart },
      { label: 'Standards', path: '/standards', icon: Scale },
      { label: 'SA Water Charges', path: '/sa-water-charges', icon: Receipt },
    ],
  },
  {
    title: 'Administration',
    admin: true,
    items: [
      { label: 'Admin Console', path: '/admin', icon: Shield, admin: true },
      { label: 'Backend Setup', path: '/backend-setup', icon: Server, admin: true },
      { label: 'DAF Validation', path: '/admin/daf-validation', icon: ShieldAlert, admin: true },
      { label: 'Secondary Valid.', path: '/admin/secondary-validation', icon: ShieldAlert, admin: true },
      { label: 'Lamella Validation', path: '/admin/lamella-validation', icon: ShieldAlert, admin: true },
      { label: 'Aeration Valid.', path: '/admin/aeration-validation', icon: ShieldAlert, admin: true },
      { label: 'GAC Validation', path: '/admin/gac-validation', icon: ShieldAlert, admin: true },
      { label: 'EQ Validation', path: '/admin/eq-validation', icon: ShieldAlert, admin: true },
      { label: 'pH Validation', path: '/admin/ph-validation', icon: ShieldAlert, admin: true },
      { label: 'Multimedia Valid.', path: '/admin/multimedia-validation', icon: ShieldAlert, admin: true },
      { label: 'Sludge Validation', path: '/admin/sludge-validation', icon: ShieldAlert, admin: true },
      { label: 'Buffer Validation', path: '/admin/buffer-validation', icon: ShieldAlert, admin: true },
    ],
  },
  {
    title: 'Account',
    items: [
      { label: 'Password', path: '/password', icon: KeyRound },
    ],
  },
];

interface SidebarProps {
  mobileOpen?: boolean;
  onMobileClose?: () => void;
}

export default function Sidebar({ mobileOpen = false, onMobileClose }: SidebarProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated, logout, isAdmin } = useAuth();
  const { activeProposal, exitProposalMode } = useProposal();
  const isMobile = useIsMobile();
  const [collapsed, setCollapsed] = useState(false);
  // Per-group expand/collapse state for collapsible NavGroups
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>(() => {
    const init: Record<string, boolean> = {};
    navGroups.forEach(g => { if (g.collapsible) init[g.title] = Boolean(g.defaultExpanded); });
    return init;
  });
  const toggleGroup = (title: string) => setExpandedGroups(s => ({ ...s, [title]: !s[title] }));

  // Auto-close mobile sidebar on route change
  useEffect(() => {
    if (isMobile && mobileOpen) {
      onMobileClose?.();
    }
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleNav = (path: string) => {
    navigate(path);
    if (isMobile) onMobileClose?.();
  };

  const NavItem = ({ item, tint }: { item: NavItem; tint?: 'blue' | 'yellow' | 'default' }) => {
    if (item.admin && !isAdmin) return null;
    // Non-clickable subsection header inside a group
    if (item.header) {
      if (collapsed) return <div className="my-1.5 mx-2 border-t border-sky-800/40" />;
      return (
        <div className="px-2 pt-2 pb-0.5 text-[10px] font-bold uppercase tracking-wider text-sky-400/60">
          {item.label}
        </div>
      );
    }
    const Icon = item.icon ?? CircleDot;
    const active = location.pathname === item.path;
    const isBlue = tint === 'blue';
    const isYellow = tint === 'yellow';
    const activeClasses = isYellow
      ? 'text-amber-200 bg-amber-500/20 border border-amber-400/50 font-semibold shadow-sm shadow-amber-900/40'
      : isBlue
        ? 'text-sky-300 bg-sky-500/15 border border-sky-500/40 font-semibold shadow-sm shadow-sky-900/40'
        : 'text-rb-brand bg-rb-brand/10 border border-rb-brand/20 font-semibold shadow-sm';
    const idleClasses = isYellow
      ? 'text-amber-300 hover:text-amber-100 hover:bg-amber-500/10 border border-transparent'
      : isBlue
        ? 'text-sky-400 hover:text-sky-200 hover:bg-sky-500/10 border border-transparent'
        : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/60 border border-transparent';
    return (
      <button
        onClick={() => handleNav(item.path!)}
        title={collapsed ? item.label : undefined}
        className={`group flex items-center gap-2.5 text-xs rounded-lg px-2.5 py-2 transition-all w-full relative ${
          active ? activeClasses : idleClasses
        } ${collapsed ? 'justify-center px-2' : ''}`}
      >
        {active && (
          <span
            className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-4 rounded-r-full"
            style={{
              background: isYellow ? '#fbbf24' : isBlue ? '#38bdf8' : 'var(--rb-brand)',
              boxShadow: isYellow ? '0 0 8px #fbbf24cc' : isBlue ? '0 0 6px #38bdf8aa' : '0 0 6px #cda42e88',
            }}
          />
        )}
        <Icon className={`shrink-0 transition-colors ${collapsed ? 'w-4 h-4' : 'w-[15px] h-[15px]'} ${
          active
            ? (isYellow ? 'text-amber-200' : isBlue ? 'text-sky-300' : 'text-rb-brand')
            : (isYellow ? 'text-amber-400 group-hover:text-amber-200' : isBlue ? 'text-sky-500 group-hover:text-sky-300' : 'text-slate-500 group-hover:text-slate-300')
        }`} />
        {!collapsed && <span className="truncate leading-tight">{item.label}</span>}
        {collapsed && (
          <div className="absolute left-full ml-2 px-2.5 py-1.5 text-xs text-slate-200 whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity z-50 rb-card-sm">
            {item.label}
          </div>
        )}
      </button>
    );
  };

  // On mobile the sidebar is a fixed overlay drawer; on desktop it's the normal aside
  const sidebarContent = (
    <aside
      className={`flex flex-col border-r border-slate-800/80 h-full transition-all duration-300
        ${isMobile
          ? 'w-[280px] shadow-2xl'
          : `shrink-0 ${collapsed ? 'w-14' : 'w-[232px]'}`
        }`}
      style={{ background: 'var(--bg-sidebar)' }}
    >
      {/* Logo */}
      <div className="flex items-center justify-between p-3 border-b border-slate-800/80 min-h-[64px]">
        <button
          onClick={() => handleNav('/')}
          className="flex items-center gap-2.5 overflow-hidden hover:opacity-80 transition-opacity"
        >
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-rb-brand to-rb-brand-dim flex items-center justify-center shrink-0 shadow-lg shadow-amber-900/30">
            <span className="text-slate-200 font-black text-xs tracking-tight">RB</span>
          </div>
          {!collapsed && (
            <div className="min-w-0">
              <h1 className="text-sm font-bold text-rb-brand leading-tight tracking-tight truncate">Reynolds & Bauhm</h1>
              <p className="text-[10px] text-slate-600 leading-tight font-medium tracking-wide uppercase">Wastewater Assessment</p>
            </div>
          )}
        </button>
        {isMobile ? (
          <button
            onClick={onMobileClose}
            className="p-1 rounded-lg text-slate-600 hover:text-slate-300 hover:bg-slate-800/50 transition-all shrink-0 ml-1"
            title="Close menu"
          >
            <X className="w-4 h-4" />
          </button>
        ) : (
          <button
            onClick={() => setCollapsed(c => !c)}
            className="p-1 rounded-lg text-slate-600 hover:text-slate-300 hover:bg-slate-800/50 transition-all shrink-0 ml-1"
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {collapsed ? <ChevronRight className="w-3.5 h-3.5" /> : <ChevronLeft className="w-3.5 h-3.5" />}
          </button>
        )}
      </div>

      {/* Proposal Mode Indicator */}
      {activeProposal && (
        <div className={`border-b border-slate-800/80 ${collapsed ? 'px-2 py-2' : 'px-3 py-2'}`}>
          {collapsed ? (
            <div className="flex justify-center relative group">
              <div className="w-8 h-8 rounded-lg bg-violet-500/20 border border-violet-500/30 flex items-center justify-center animate-pulse">
                <FileText className="w-4 h-4 text-violet-400" />
              </div>
              <div className="absolute left-full ml-2 px-2.5 py-1.5 bg-rb-surface border border-violet-500/30 rounded-lg text-[10px] text-violet-300 whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity z-50 shadow-xl">
                Proposal Mode: {activeProposal.proposalNumber}
              </div>
            </div>
          ) : (
            <div className="bg-violet-500/10 border border-violet-500/20 rounded-lg px-2.5 py-2 space-y-1.5">
              <div className="flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-violet-400 animate-pulse" />
                <span className="text-[10px] font-bold text-violet-400 uppercase tracking-wider">Proposal Mode</span>
              </div>
              <p className="text-[10px] font-bold text-violet-300 font-mono truncate">{activeProposal.proposalNumber}</p>
              <p className="text-[10px] text-slate-500 truncate">{activeProposal.title}</p>
              <button
                onClick={exitProposalMode}
                className="w-full text-[10px] font-bold text-rose-400 hover:text-rose-300 flex items-center gap-1 mt-1"
              >
                <span>Exit Mode</span>
              </button>
            </div>
          )}
        </div>
      )}

      {/* Navigation groups — when there's an active customer/proposal, any
          group tinted 'yellow' (the MYLAR job) is pinned to the very top. */}
      <nav className="flex-1 overflow-y-auto py-2 space-y-0 scrollbar-thin">
        {(() => {
          const hasActive = Boolean(activeProposal);
          const yellowGroups = navGroups.filter(g => g.tint === 'yellow');
          const rest = navGroups.filter(g => g.tint !== 'yellow');
          // When a job is active, pin yellow groups to the top; otherwise keep their original spot
          const ordered = hasActive ? [...yellowGroups, ...rest] : navGroups;
          return ordered;
        })().map((group) => {
          if (group.admin && !isAdmin) return null;
          const visibleItems = group.items.filter(item => !item.admin || isAdmin);
          if (visibleItems.length === 0) return null;
          const isBlueGroup = group.tint === 'blue';
          const isYellowGroup = group.tint === 'yellow';
          const isCollapsible = Boolean(group.collapsible);
          const isExpanded = !isCollapsible || (expandedGroups[group.title] ?? Boolean(group.defaultExpanded));
          const containerClasses = isYellowGroup
            ? 'pt-1 pb-2 rounded-xl bg-amber-950/30 border-2 border-amber-500/40 mx-1 shadow-lg shadow-amber-900/30'
            : isBlueGroup
              ? 'pt-1 pb-2 rounded-xl bg-sky-950/30 border border-sky-900/40 mx-1'
              : '';
          return (
            <div key={group.title} className={`mb-1 ${containerClasses}`}>
              {!collapsed && (
                isCollapsible ? (
                  <button
                    onClick={() => toggleGroup(group.title)}
                    className="w-full flex items-center justify-between gap-2 rb-nav-group hover:text-slate-300 cursor-pointer transition-colors"
                  >
                    <span>{group.title.replace(/[▾▸]\s*$/, '').trim()}</span>
                    {isExpanded
                      ? <ChevronLeft className="w-3 h-3 rotate-[270deg]" />
                      : <ChevronRight className="w-3 h-3" />}
                  </button>
                ) : (
                  <div className={isYellowGroup
                    ? 'px-2.5 pt-1.5 pb-1 text-[11px] font-extrabold tracking-widest text-amber-300 uppercase drop-shadow-[0_0_6px_rgba(251,191,36,0.55)]'
                    : isBlueGroup
                      ? 'px-2.5 pt-1.5 pb-1 text-[11px] font-extrabold tracking-widest text-sky-300 uppercase drop-shadow-[0_0_4px_rgba(56,189,248,0.4)]'
                      : 'rb-nav-group'}>
                    {group.title}
                  </div>
                )
              )}
              {collapsed && <div className={`my-1 mx-2 border-t ${isYellowGroup ? 'border-amber-500/50' : isBlueGroup ? 'border-sky-700/40' : 'border-slate-800/60'}`} />}
              {(isExpanded || collapsed) && (
                <div className="px-1.5 space-y-0.5">
                  {visibleItems.map((item, idx) => (
                    <NavItem key={item.path ?? `hdr-${item.label}-${idx}`} item={item} tint={group.tint} />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </nav>

      {/* Bottom: user + logout */}
      <div className="border-t border-slate-800/80 p-2 space-y-1">
        <button
          onClick={() => handleNav('/profile')}
          title={collapsed ? 'My Profile' : undefined}
          className={`group flex items-center gap-2.5 text-xs text-slate-500 hover:text-cyan-400 transition-all rounded-lg px-2.5 py-2 hover:bg-slate-800/60 w-full ${collapsed ? 'justify-center' : ''}`}
        >
          <UserCircle className="w-4 h-4 shrink-0" />
          {!collapsed && <span>My Profile</span>}
        </button>

        {/* User block */}
        {isAuthenticated && user && !collapsed && (
          <div className="flex items-center gap-2 px-2.5 py-2 rounded-lg bg-slate-800/30 border border-slate-800/60">
            <div className="w-7 h-7 rounded-full bg-gradient-to-br from-rb-brand to-rb-brand-dim text-slate-200 flex items-center justify-center text-xs font-black flex-shrink-0 shadow-md">
              {(user.name || user.username || 'U').charAt(0).toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs font-semibold text-slate-200 truncate leading-tight">{user.name || user.username}</p>
              <p className="text-[10px] text-slate-500 leading-tight">{user.role === 'admin' ? '⚡ Administrator' : 'User'}</p>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 rb-pulse-dot" />
            </div>
          </div>
        )}

        {isAuthenticated && user && collapsed && (
          <div className="flex justify-center py-1 relative group">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-rb-brand to-rb-brand-dim text-slate-200 flex items-center justify-center text-xs font-black shadow-md">
              {(user.name || user.username || 'U').charAt(0).toUpperCase()}
            </div>
            <span className="absolute bottom-1 right-1 w-2 h-2 rounded-full bg-emerald-500 border-2 border-[#0f172a]" />
          </div>
        )}

        {!isAuthenticated && !collapsed && (
          <div className="flex items-center gap-2 px-2.5 py-2 rounded-lg bg-slate-800/20 border border-slate-800/40">
            <div className="w-7 h-7 rounded-full bg-slate-700 flex items-center justify-center shrink-0">
              <UserCircle className="w-4 h-4 text-slate-500" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs text-slate-400">Not signed in</p>
            </div>
            <span className="w-1.5 h-1.5 rounded-full bg-red-500 shrink-0" />
          </div>
        )}

        <button
          onClick={logout}
          title={collapsed ? 'Logout' : undefined}
          className={`group flex items-center gap-2.5 text-xs text-slate-600 hover:text-red-400 transition-all rounded-lg px-2.5 py-2 hover:bg-red-500/5 w-full ${collapsed ? 'justify-center' : ''}`}
        >
          <LogOut className="w-3.5 h-3.5 shrink-0" />
          {!collapsed && <span>Logout</span>}
        </button>
      </div>
    </aside>
  );

  if (isMobile) {
    return (
      <>
        {/* Dark backdrop */}
        {mobileOpen && (
          <div
            className="fixed inset-0 z-[150] bg-black/60 backdrop-blur-sm"
            onClick={onMobileClose}
          />
        )}
        {/* Drawer — slides in from left */}
        <div
          className={`fixed left-0 top-0 z-[160] h-full transition-transform duration-300 ease-in-out ${
            mobileOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
        >
          {sidebarContent}
        </div>
      </>
    );
  }

  return sidebarContent;
}
